import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Variants, motion, useAnimation } from 'framer-motion'
import { Base } from '@itsrever/design-system'
import { AIWidget } from '../../ai-widget/ai-widget'
import { useReturnRateInsights } from '@/hooks/use-return-rate-insights'
import { useEcommerceId } from '@/hooks'
import { ReturnRateAIInsightsContent } from './return-rate-ai-insights-content'
import { usePostHog } from 'posthog-js/react'

const { Button } = Base

export const ReturnRateAIInsights = () => {
  const pageContentEl = document.getElementById('page-content')

  const posthog = usePostHog()

  const { i18n, t } = useTranslation()

  const ecommerceId = useEcommerceId()

  const { data, status } = useReturnRateInsights({
    ecommerceId,
    language: i18n.language
  })

  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (data?.content_insights)
      posthog?.capture('Loaded return rate insights', {
        ecommerceId
      })
  }, [data, ecommerceId, posthog])

  const onOpenInsights = () => {
    setOpen((prev) => !prev)

    posthog?.capture('Opened return rate insights', {
      ecommerceId
    })
  }

  if (status === 'pending') {
    return (
      <div
        className="h-[38px] w-[105.2px] animate-pulse rounded-lg bg-neutral-100"
        role="status"
        aria-label="Loading"
      />
    )
  }

  if (status === 'error') {
    return null
  }

  if (!data.content_insights) {
    return null
  }

  return (
    <>
      <AIInsightsButton onOpen={onOpenInsights} />
      {pageContentEl &&
        createPortal(
          <div
            data-open={open}
            className="group relative inset-0 h-full w-0 min-w-0 max-w-[450px] overflow-hidden rounded-[20px] bg-white transition-all duration-0 data-[open=true]:ml-1.5 data-[open=true]:w-full data-[open=true]:duration-[400ms]"
          >
            <div className="relative flex h-full w-[450px] flex-col">
              <button
                className="absolute right-3 top-3 rounded-md p-1 hover:bg-neutral-100"
                onClick={() => setOpen(false)}
              >
                <svg
                  width="20px"
                  height="20px"
                  strokeWidth="1.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  color="#000000"
                >
                  <path
                    d="M6.75827 17.2426L12.0009 12M17.2435 6.75736L12.0009 12M12.0009 12L6.75827 6.75736M12.0009 12L17.2435 17.2426"
                    stroke="#000000"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </svg>
              </button>
              <header className="flex items-center gap-3 border-b border-solid border-neutral-200 bg-gradient-to-br from-blue-200 via-blue-50/60 px-5 py-3">
                <AIWidget />
                <div className="space-y-0.5">
                  <h2 className="font-[Roobert] text-base font-medium">
                    {t('returns_analytics.ai_insights_description')}
                  </h2>
                </div>
              </header>
              {open ? <ReturnRateAIInsightsContent data={data} /> : null}
            </div>
          </div>,
          pageContentEl
        )}
    </>
  )
}

const sparkleVariants: Variants = {
  initial: {
    y: 0,
    fill: 'none'
  },
  hover: {
    y: [0, -1, 0, 0],
    fill: 'currentColor',
    transition: {
      duration: 1,
      bounce: 0.3
    }
  }
}

const starVariants: Variants = {
  initial: {
    opacity: 1,
    x: 0,
    y: 0
  },
  blink: () => ({
    opacity: [0, 1, 0, 0, 0, 0, 1],
    transition: {
      duration: 2,
      type: 'spring',
      stiffness: 70,
      damping: 10,
      mass: 0.4
    }
  })
}

const AIInsightsButton = ({ onOpen }: { onOpen: () => void }) => {
  const starControls = useAnimation()
  const sparkleControls = useAnimation()

  const handleMouseEnter = useCallback(() => {
    sparkleControls.start('hover')
    starControls.start('blink', { delay: 1 })
  }, [sparkleControls, starControls])

  const handleMouseLeave = useCallback(() => {
    sparkleControls.start('initial')
    starControls.start('initial')
  }, [sparkleControls, starControls])

  return (
    <motion.div
      className="relative inline-flex"
      whileHover="hover"
      initial="rest"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.span
        className="absolute inset-0 rounded-lg bg-[#1790ff] opacity-0 blur-sm"
        variants={{
          rest: { opacity: 0, scale: 0.95 },
          hover: { opacity: 0.7, scale: 0.97 }
        }}
        transition={{ duration: 0.3 }}
      />
      <Button
        variant="secondary"
        onClick={onOpen}
        className="relative bg-white hover:border-[#1790ff]/70 hover:!bg-white hover:!shadow-none"
      >
        <span className="text-primary-main">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={16}
            height={16}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <motion.path
              d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"
              variants={sparkleVariants}
              animate={sparkleControls}
            />
            <motion.path
              d="M20 3v4"
              variants={starVariants}
              animate={starControls}
            />
            <motion.path
              d="M22 5h-4"
              variants={starVariants}
              animate={starControls}
            />
            <motion.path
              d="M4 17v2"
              variants={starVariants}
              animate={starControls}
            />
            <motion.path
              d="M5 18H3"
              variants={starVariants}
              animate={starControls}
            />
          </svg>{' '}
        </span>
        AI Insights
      </Button>
    </motion.div>
  )
}
